import React, { useEffect, useState } from "react";

import {
  GetReportPayroll,
  GetReportPayrollXlsx,
} from "../../api/report";
import { GetAcativeRestaurants } from "../../api/restaurant";

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

const ReportPayroll = ({ token }) => {
  const [startDate, setStartDate] = useState(getMonthStartDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [restaurantId, setRestaurantId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadRestaurants() {
      const restaurants = await GetAcativeRestaurants(token);
      if (Array.isArray(restaurants)) {
        setRestaurantList(restaurants);
      }
    }
    loadRestaurants();
  }, [token]);

  async function loadReport() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (startDate > endDate) {
      setError("Начало периода не может быть позже конца");
      return;
    }

    setLoading(true);
    setError("");
    const response = await GetReportPayroll(
      token,
      startDate,
      endDate,
      restaurantId,
    );
    setLoading(false);

    if (response?.detail) {
      setError(response.detail);
      setReport(null);
      return;
    }
    setReport(response);
  }

  async function downloadReport() {
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }

    setError("");
    const response = await GetReportPayrollXlsx(
      token,
      startDate,
      endDate,
      restaurantId,
    );
    if (!response?.data) {
      setError("Не удалось скачать отчёт");
      return;
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers["content-disposition"];
    let filename = "payroll.xlsx";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/\.xlsx_?[^.]*$/, ".xlsx");
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  return (
    <div className="reports-page container">
      <h1>Расчёт ЗП</h1>
      <p className="text-muted">
        Часы считаются по отметкам Boss Control в рамках запланированной смены.
        Ставка — из последней отметки за период. МНГ не включаются.
      </p>

      <div className="row g-3 align-items-end mb-4">
        <div className="col-md-3">
          <label className="form-label">Начало периода</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Конец периода</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">Ресторан</label>
          <select
            className="form-select"
            value={restaurantId}
            onChange={(e) => setRestaurantId(Number(e.target.value))}
          >
            <option value={0}>Все рестораны</option>
            {restaurantList.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 d-flex gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={loadReport}
            disabled={loading}
          >
            {loading ? "Загрузка…" : "Показать"}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={downloadReport}
          >
            Excel
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {report && (
        <>
          <div className="table-responsive mb-4">
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>День</th>
                  {report.day_labels.map((label) => (
                    <th key={label} className="text-center">
                      {label}
                    </th>
                  ))}
                  <th className="text-center">Итого</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Всего</td>
                  {report.daily_summary.map((day) => (
                    <td key={day.date} className="text-center">
                      {day.total_hours || ""}
                    </td>
                  ))}
                  <td className="text-center fw-bold">
                    {report.daily_totals.total_hours}
                  </td>
                </tr>
                <tr>
                  <td>День</td>
                  {report.daily_summary.map((day) => (
                    <td key={day.date} className="text-center">
                      {day.day_hours || ""}
                    </td>
                  ))}
                  <td className="text-center fw-bold">
                    {report.daily_totals.day_hours}
                  </td>
                </tr>
                <tr>
                  <td>Ночь</td>
                  {report.daily_summary.map((day) => (
                    <td key={day.date} className="text-center">
                      {day.night_hours || ""}
                    </td>
                  ))}
                  <td className="text-center fw-bold">
                    {report.daily_totals.night_hours}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Должность</th>
                  <th>Табельный №</th>
                  <th>Стаж, дней</th>
                  <th>Подразделение</th>
                  <th>Ставка</th>
                  <th>Дн. часы</th>
                  <th>Ночь</th>
                  <th>Итого ч.</th>
                  <th>Сумма день</th>
                  <th>Сумма ночь</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {report.employees.map((employee) => (
                  <tr key={employee.user_id}>
                    <td>{employee.full_name}</td>
                    <td>{employee.post_name}</td>
                    <td>{employee.personnel_number}</td>
                    <td className="text-center">{employee.tenure_days}</td>
                    <td>{employee.restaurant_name}</td>
                    <td className="text-end">{employee.rate}</td>
                    <td className="text-end">{employee.day_hours}</td>
                    <td className="text-end">{employee.night_hours}</td>
                    <td className="text-end">{employee.total_hours}</td>
                    <td className="text-end">{employee.day_amount}</td>
                    <td className="text-end">{employee.night_amount}</td>
                    <td className="text-end fw-bold">{employee.total_amount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td colSpan={6}>Итого</td>
                  <td className="text-end">{report.totals.day_hours}</td>
                  <td className="text-end">{report.totals.night_hours}</td>
                  <td className="text-end">{report.totals.total_hours}</td>
                  <td className="text-end">{report.totals.day_amount}</td>
                  <td className="text-end">{report.totals.night_amount}</td>
                  <td className="text-end">{report.totals.total_amount}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportPayroll;
